import { IUseWalletStore } from '@/src/store/global/types';
import {
    DelegationCreateTxParams,
    DelegationDeleteTxParams,
    DelegationDepositTxParams,
    DelegationUpdateMinADATxParams,
    DelegationWithdrawTxParams,
    explainErrorTx,
    pushSucessNotification,
    pushWarningNotification,
} from '../../Commons';
import { ProtocolEntity } from '../../Entities';
import { DelegationEntity } from '../../Entities/Delegation.Entity';
import { LucidToolsFrontEnd } from '../../lib';
import { SmartDBBtnHandlers } from '../index.exports.BtnHandlers.TxApiCalls';
import { DelegationTxApiCalls } from './Delegation.FrontEnd.Tx.Api.Calls';

export class DelegationBtnHandlers extends SmartDBBtnHandlers {
    protected static _Entity = DelegationEntity;
    protected static _TxApiCalls = DelegationTxApiCalls;

    // #region class methods

    // #endregion class methods

    // #region front end Btn tx handlers

    public static async handleBtnCreateTx<T extends DelegationEntity>(
        setProcessingTxMessage: (value: string) => void,
        setProcessingTxHash: (value: string) => void,
        walletStore: IUseWalletStore,
        protocol: ProtocolEntity,
        txParams: DelegationCreateTxParams
    ): Promise<boolean> {
        try {
            //--------------------------------------
            setProcessingTxMessage('Creating Delegation...');
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const { txCborHex } = await this._TxApiCalls.createTxApi(walletTxParams, txParams);
            //--------------------------------------
            setProcessingTxMessage('Transaction prepared, waiting for sign to submit...');
            //--------------------------------------
            const txHash = await LucidToolsFrontEnd.signAndSubmitTx(lucid, txCborHex, walletStore.info, emulatorDB, walletStore.swDoNotPromtForSigning);
            //--------------------------------------
            setProcessingTxMessage(`Transaction submitted, waiting for confirmation...`);
            setProcessingTxHash(txHash);
            //--------------------------------------
            if (await LucidToolsFrontEnd.awaitTx(lucid, txHash)) {
                console.log(`${this._Entity.className()}] - handleBtnCreateTx - waitForTxConfirmation - Tx confirmed - ${txHash}`);
                pushSucessNotification(`${this._Entity.className()} Create Tx`, txHash, true);
            } else {
                console.log(`${this._Entity.className()}] - handleBtnCreateTx - waitForTxConfirmation - Tx not confirmed - ${txHash}`);
                throw `Tx not confirmed`;
            }
            //--------------------------------------
            setProcessingTxMessage(`Syncronizing...`);
            //--------------------------------------
            await walletStore.loadWalletData();
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnCreateTx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Create Tx`, error_explained);
            setProcessingTxMessage(error_explained);
            return false;
        }
    }

    public static async handleBtnDepositTx<T extends DelegationEntity>(
        setProcessingTxMessage: (value: string) => void,
        setProcessingTxHash: (value: string) => void,
        walletStore: IUseWalletStore,
        protocol: ProtocolEntity,
        txParams: DelegationDepositTxParams
    ): Promise<boolean> {
        try {
            //--------------------------------------
            setProcessingTxMessage('Preparing Deposit...');
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const { txCborHex } = await this._TxApiCalls.depositTxApi(walletTxParams, txParams);
            //--------------------------------------
            setProcessingTxMessage('Transaction prepared, waiting for sign to submit...');
            //--------------------------------------
            const txHash = await LucidToolsFrontEnd.signAndSubmitTx(lucid, txCborHex, walletStore.info, emulatorDB, walletStore.swDoNotPromtForSigning);
            //--------------------------------------
            setProcessingTxMessage(`Transaction submitted, waiting for confirmation...`);
            setProcessingTxHash(txHash);
            //--------------------------------------
            if (await LucidToolsFrontEnd.awaitTx(lucid, txHash)) {
                console.log(`${this._Entity.className()}] - handleBtnDepositTx - waitForTxConfirmation - Tx confirmed - ${txHash}`);
                pushSucessNotification(`${this._Entity.className()} Deposit Tx`, txHash, true);
            } else {
                console.log(`${this._Entity.className()}] - handleBtnDepositTx - waitForTxConfirmation - Tx not confirmed - ${txHash}`);
                throw `Tx not confirmed`;
            }
            //--------------------------------------
            setProcessingTxMessage(`Syncronizing...`);
            //--------------------------------------
            await walletStore.loadWalletData();
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnDepositTx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Deposit Tx`, error_explained);
            setProcessingTxMessage(error_explained);
            return false;
        }
    }

    public static async handleBtnWithdrawTx<T extends DelegationEntity>(
        setProcessingTxMessage: (value: string) => void,
        setProcessingTxHash: (value: string) => void,
        walletStore: IUseWalletStore,
        protocol: ProtocolEntity,
        txParams: DelegationWithdrawTxParams
    ): Promise<boolean> {
        try {
            //--------------------------------------
            setProcessingTxMessage('Preparing Withdraw...');
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const { txCborHex } = await this._TxApiCalls.withdrawTxApi(walletTxParams, txParams);
            //--------------------------------------
            setProcessingTxMessage('Transaction prepared, waiting for sign to submit...');
            //--------------------------------------
            const txHash = await LucidToolsFrontEnd.signAndSubmitTx(lucid, txCborHex, walletStore.info, emulatorDB, walletStore.swDoNotPromtForSigning);
            //--------------------------------------
            setProcessingTxMessage(`Transaction submitted, waiting for confirmation...`);
            setProcessingTxHash(txHash);
            //--------------------------------------
            if (await LucidToolsFrontEnd.awaitTx(lucid, txHash)) {
                console.log(`${this._Entity.className()}] - handleBtnWithdrawTx - waitForTxConfirmation - Tx confirmed - ${txHash}`);
                pushSucessNotification(`${this._Entity.className()} Withdraw Tx`, txHash, true);
            } else {
                console.log(`${this._Entity.className()}] - handleBtnWithdrawTx - waitForTxConfirmation - Tx not confirmed - ${txHash}`);
                throw `Tx not confirmed`;
            }
            //--------------------------------------
            setProcessingTxMessage(`Syncronizing...`);
            //--------------------------------------
            await walletStore.loadWalletData();
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnWithdrawTx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Withdraw Tx`, error_explained);
            setProcessingTxMessage(error_explained);
            return false;
        }
    }

    public static async handleBtnUpdateMinADATx<T extends DelegationEntity>(
        setProcessingTxMessage: (value: string) => void,
        setProcessingTxHash: (value: string) => void,
        walletStore: IUseWalletStore,
        protocol: ProtocolEntity,
        txParams: DelegationUpdateMinADATxParams
    ): Promise<boolean> {
        try {
            //--------------------------------------
            setProcessingTxMessage('Updating Min ADA ...');
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const { txCborHex } = await this._TxApiCalls.updateMinADATxApi(walletTxParams, txParams);
            //--------------------------------------
            setProcessingTxMessage('Transaction prepared, waiting for sign to submit...');
            //--------------------------------------
            const txHash = await LucidToolsFrontEnd.signAndSubmitTx(lucid, txCborHex, walletStore.info, emulatorDB, walletStore.swDoNotPromtForSigning);
            //--------------------------------------
            setProcessingTxMessage(`Transaction submitted, waiting for confirmation...`);
            setProcessingTxHash(txHash);
            //--------------------------------------
            if (await LucidToolsFrontEnd.awaitTx(lucid, txHash)) {
                console.log(`${this._Entity.className()}] - handleBtnUpdateMinADATx - waitForTxConfirmation - Tx confirmed - ${txHash}`);
                pushSucessNotification(`${this._Entity.className()} Update Min ADA Tx`, txHash, true);
            } else {
                console.log(`${this._Entity.className()}] - handleBtnUpdateMinADATx - waitForTxConfirmation - Tx not confirmed - ${txHash}`);
                throw `Tx not confirmed`;
            }
            //--------------------------------------
            setProcessingTxMessage(`Syncronizing...`);
            //--------------------------------------
            await walletStore.loadWalletData();
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnUpdateMinADATx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Update Min ADA Tx`, error_explained);
            setProcessingTxMessage(error_explained);
            return false;
        }
    }

    public static async handleBtnDeleteTx<T extends DelegationEntity>(
        setProcessingTxMessage: (value: string) => void,
        setProcessingTxHash: (value: string) => void,
        walletStore: IUseWalletStore,
        protocol: ProtocolEntity,
        txParams: DelegationDeleteTxParams
    ): Promise<boolean> {
        try {
            //--------------------------------------
            setProcessingTxMessage('Deleting...');
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const { txCborHex } = await this._TxApiCalls.deleteTxApi(walletTxParams, txParams);
            //--------------------------------------
            setProcessingTxMessage('Transaction prepared, waiting for sign to submit...');
            //--------------------------------------
            const txHash = await LucidToolsFrontEnd.signAndSubmitTx(lucid, txCborHex, walletStore.info, emulatorDB, walletStore.swDoNotPromtForSigning);
            //--------------------------------------
            setProcessingTxMessage(`Transaction submitted, waiting for confirmation...`);
            setProcessingTxHash(txHash);
            //--------------------------------------
            if (await LucidToolsFrontEnd.awaitTx(lucid, txHash)) {
                console.log(`${this._Entity.className()}] - handleBtnDeleteTx - waitForTxConfirmation - Tx confirmed - ${txHash}`);
                pushSucessNotification(`${this._Entity.className()} Delete Tx`, txHash, true);
            } else {
                console.log(`${this._Entity.className()}] - handleBtnDeleteTx - waitForTxConfirmation - Tx not confirmed - ${txHash}`);
                throw `Tx not confirmed`;
            }
            //--------------------------------------
            setProcessingTxMessage(`Syncronizing...`);
            //--------------------------------------
            await walletStore.loadWalletData();
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnDeleteTx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Delete Tx`, error_explained);
            setProcessingTxMessage(error_explained);
            return false;
        }
    }

    // #endregion front end Btn tx handlers
}
